/**
 * Локальное хранилище: кеш и очередь синхронизации.
 *
 * Зал — это подвал без связи. Подход обязан записаться мгновенно и локально,
 * а на сервер уехать когда получится. Идемпотентность обеспечивает
 * `client_uuid`: повторная отправка не создаёт дубликат (раздел 9).
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { SetInput, Today } from '../api/types'

export type QueueStatus = 'pending' | 'failed'

export interface QueuedSet {
  /** Он же ключ записи и он же `client_uuid` запроса. */
  uuid: string
  payload: SetInput
  createdAt: number
  attempts: number
  status: QueueStatus
  lastError?: string
}

interface FitnessDB extends DBSchema {
  queue: {
    key: string
    value: QueuedSet
    indexes: { 'by-status': QueueStatus; 'by-created': number }
  }
  cache: {
    key: string
    value: { key: string; value: unknown; savedAt: number }
  }
}

const DB_NAME = 'fitness-companion'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase<FitnessDB>> | null = null

export function db(): Promise<IDBPDatabase<FitnessDB>> {
  if (!dbPromise) {
    dbPromise = openDB<FitnessDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        const queue = database.createObjectStore('queue', { keyPath: 'uuid' })
        queue.createIndex('by-status', 'status')
        queue.createIndex('by-created', 'createdAt')
        database.createObjectStore('cache', { keyPath: 'key' })
      },
    })
  }
  return dbPromise
}

// --------------------------------------------------------------------------- //
// Очередь
// --------------------------------------------------------------------------- //

export async function enqueue(item: QueuedSet): Promise<void> {
  const database = await db()
  await database.put('queue', item)
}

export async function dequeue(uuid: string): Promise<void> {
  const database = await db()
  await database.delete('queue', uuid)
}

export async function queuedItems(): Promise<QueuedSet[]> {
  const database = await db()
  const all = await database.getAll('queue')
  // По времени создания: подходы должны уезжать в том порядке, в котором
  // человек их сделал, иначе номера подходов в истории перепутаются.
  return all.sort((a, b) => a.createdAt - b.createdAt)
}

export async function pendingCount(): Promise<number> {
  const items = await queuedItems()
  return items.filter((i) => i.status === 'pending').length
}

export async function failedItems(): Promise<QueuedSet[]> {
  const items = await queuedItems()
  return items.filter((i) => i.status === 'failed')
}

// --------------------------------------------------------------------------- //
// Кеш экранов
// --------------------------------------------------------------------------- //

/**
 * Последний успешный ответ экрана «Сегодня».
 *
 * Нужен ровно для одного: открыть приложение в зале без связи и увидеть свою
 * тренировку, а не спиннер. Данные помечены временем — экран честно говорит,
 * что они не свежие.
 */
export async function saveCache(key: string, value: unknown): Promise<void> {
  const database = await db()
  await database.put('cache', { key, value, savedAt: Date.now() })
}

export async function readCache<T>(key: string): Promise<{ value: T; savedAt: number } | null> {
  const database = await db()
  const row = await database.get('cache', key)
  if (!row) return null
  return { value: row.value as T, savedAt: row.savedAt }
}

export const CACHE_TODAY = 'today'

export type CachedToday = { value: Today; savedAt: number }
